import { useMemo, useState } from 'react';
import { Calendar, Check, Copy, ExternalLink, Globe, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import type { UrlContent } from '@vivport/backend/types';

interface DetailsUrlProps {
  content: UrlContent;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
}

export function DetailsUrl({ content, onDescriptionSave, isRegenerating }: DetailsUrlProps) {
  const [copied, setCopied] = useState(false);

  const isUsable = content.url.trim().length > 0;

  const parsedDomain = useMemo<string | null>(() => {
    if (!isUsable) return null;
    try {
      return new URL(content.url).hostname;
    } catch {
      return null;
    }
  }, [content.url, isUsable]);

  const handleCopy = async () => {
    if (!isUsable) return;
    try {
      await navigator.clipboard.writeText(content.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable in non-secure contexts
    }
  };

  const creatorName =
    [content.createdBy.firstName, content.createdBy.surname].filter(Boolean).join(' ').trim() ||
    'Unknown';

  const CopyIcon = copied ? Check : Copy;

  return (
    <div data-testid="details-url" className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {isUsable ? (parsedDomain ?? content.url) : 'URL not available'}
          </span>
        </div>

        <div className="bg-muted rounded px-3 py-2 font-mono text-sm break-all">
          {isUsable ? content.url : '—'}
        </div>

        <div className="flex flex-wrap gap-2">
          {isUsable ? (
            <Button asChild>
              <a href={content.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open in new tab
              </a>
            </Button>
          ) : (
            <Button disabled>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in new tab
            </Button>
          )}
          <Button variant="outline" type="button" onClick={handleCopy} disabled={!isUsable}>
            <CopyIcon className="mr-2 h-4 w-4" />
            {copied ? 'Copied' : 'Copy URL'}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Description</h3>
        <ContentDescriptionEditable
          description={content.description || ''}
          onSave={onDescriptionSave}
          disabled={isRegenerating}
          placeholder={isRegenerating ? 'Regeneration currently in progress' : 'Click to add description'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          Saved {new Date(content.originCreatedAt).toLocaleString()}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <User className="h-3.5 w-3.5" />
          by {creatorName}
        </span>
      </div>
    </div>
  );
}
